import { convertAACaretPositionOrRangeToDna } from "@teselagen/sequence-utils";
import { gbDivisions, untitledSequenceName } from "./utils/constants";
import flattenSequenceArray from "./utils/flattenSequenceArray";
import validateSequenceArray from "./utils/validateSequenceArray";
import splitStringIntoLines from "./utils/splitStringIntoLines.js";
import createInitialSequence from "./utils/createInitialSequence";
function wrapOriginSpanningFeatures(locArrayInput, sequenceLength, inclusive1BasedStart, inclusive1BasedEnd) {
  const locArrayOutput = locArrayInput.map((loc) => ({ ...loc }));
  for (let i = 0; i < locArrayOutput.length - 1; i++) {
    const firstFeature = locArrayOutput[i];
    const secondFeature = locArrayOutput[i + 1];
    if (firstFeature.end === sequenceLength - (inclusive1BasedEnd ? 0 : 1) && secondFeature.start === 1 - (inclusive1BasedStart ? 0 : 1)) {
      locArrayOutput[i] = {
        start: firstFeature.start,
        end: secondFeature.end
      };
      locArrayOutput.splice(i + 1, 1);
    }
  }
  return locArrayOutput;
}
export function parseFeatureLocation(locStr, isProtein, inclusive1BasedStart, inclusive1BasedEnd, isCircular, sequenceLength) {
  locStr = locStr.trim();
  const positionsArray = [];
  const locationParts = locStr.split(",");
  locationParts.forEach((locPart) => {
    const extractedPositions = locPart.match(/(\d+)/g);
    if (extractedPositions === null) {
      return;
    }
    positionsArray.push(extractedPositions[0]);
    positionsArray.push(extractedPositions[1] || extractedPositions[0]);
  });
  const locArray = [];
  for (let i = 0; i < positionsArray.length; i += 2) {
    const start = parseInt(positionsArray[i], 10) - (inclusive1BasedStart ? 0 : 1);
    const end = parseInt(positionsArray[i + 1], 10) - (inclusive1BasedEnd ? 0 : 1);
    const location = {
      start,
      end
    };
    locArray.push(
      isProtein ? convertAACaretPositionOrRangeToDna(location) : location
    );
  }
  if (isCircular && sequenceLength) {
    return wrapOriginSpanningFeatures(
      locArray,
      sequenceLength,
      inclusive1BasedStart,
      inclusive1BasedEnd
    );
  } else {
    return locArray;
  }
}
function genbankToJson(string, options = {}) {
  const {
    inclusive1BasedStart,
    inclusive1BasedEnd
    //these are also valid options:
    // primersAsFeatures,
    // sequenceTypeFromLocus,
    // isProtein,
  } = options;
  const resultsArray = [];
  let result;
  let currentFeatureNote;
  const genbankAnnotationKey = {
    LOCUS_TAG: "LOCUS",
    DEFINITION_TAG: "DEFINITION",
    ACCESSION_TAG: "ACCESSION",
    VERSION_TAG: "VERSION",
    KEYWORDS_TAG: "KEYWORDS",
    //SEGMENT_TAG:"SEGMENT"
    SOURCE_TAG: "SOURCE",
    ORGANISM_TAG: "ORGANISM",
    REFERENCE_TAG: "REFERENCE",
    AUTHORS_TAG: "AUTHORS",
    CONSORTIUM_TAG: "CONSRTM",
    TITLE_TAG: "TITLE",
    JOURNAL_TAG: "JOURNAL",
    PUBMED_TAG: "PUBMED",
    REMARK_TAG: "REMARK",
    COMMENT_TAG: "COMMENT",
    FEATURES_TAG: "FEATURES",
    BASE_COUNT_TAG: "BASE COUNT",
    //CONTIG_TAG: "CONTIG"
    ORIGIN_TAG: "ORIGIN",
    END_SEQUENCE_TAG: "//"
  };
  let hasFoundLocus = false;
  let featureLocationIndentation;
  try {
    const lines = splitStringIntoLines(string);
    let LINETYPE = false;
    if (lines === null) {
      addMessage("Import Error: Sequence file is empty");
    }
    lines.some(function(line) {
      if (line === null) {
        return true;
      }
      const shouldUseSpaceAsDelimiter = !line.trim().startsWith("/");
      const key = getLineKey(line, shouldUseSpaceAsDelimiter);
      const val = getLineVal(line, shouldUseSpaceAsDelimiter);
      const isKeyRunon = isKeywordRunon(line);
      const isSubKey = isSubKeyword(line);
      const isKey = isKeyword(line);
      if (!isKeyRunon) {
        if (key === "LOCUS") {
          LINETYPE = key;
        } else if (key === "REFERENCE") {
          LINETYPE = key;
        } else if (key === "FEATURES") {
          LINETYPE = key;
        } else if (key === "ORIGIN") {
          LINETYPE = key;
        } else if (key === "//") {
          LINETYPE = key;
        } else if (isKey === true) {
          LINETYPE = key;
        }
      }
      if (line.trim() === "" || key === ";") {
        return false;
      }
      if (!hasFoundLocus && LINETYPE !== genbankAnnotationKey.LOCUS_TAG) {
        return true;
      }
      switch (LINETYPE) {
        case genbankAnnotationKey.LOCUS_TAG:
          if (hasFoundLocus) {
            line = hasFoundLocus + line;
          }
          parseLocus(line);
          hasFoundLocus = line;
          break;
        case genbankAnnotationKey.FEATURES_TAG:
          if (val === "") {
            addMessage(
              "Warning: The feature '" + key + "'' has no location specified. This line has been ignored: line" + line
            );
            break;
          }
          parseFeatures(line, key, val);
          break;
        case genbankAnnotationKey.ORIGIN_TAG:
          parseOrigin(line, key);
          break;
        case genbankAnnotationKey.END_SEQUENCE_TAG:
          endSeq(options);
          break;
        case genbankAnnotationKey.DEFINITION_TAG:
          line = line.replace(/DEFINITION/, "");
          line = line.trim();
          if (result.parsedSequence) {
            if (result.parsedSequence.definition) {
              result.parsedSequence.definition += " " + line;
            } else {
              result.parsedSequence.definition = line;
            }
            if (result.parsedSequence.description) {
              result.parsedSequence.description += " " + line;
            } else {
              result.parsedSequence.description = line;
            }
          } else {
            throw new Error(
              "no sequence yet created upon which to extract an extra line!"
            );
          }
          break;
        case genbankAnnotationKey.ACCESSION_TAG:
          line = line.replace(/ACCESSION/, "");
          line = line.trim();
          if (result.parsedSequence) {
            result.parsedSequence.accession = line;
          }
          break;
        case genbankAnnotationKey.VERSION_TAG:
          line = line.replace(/VERSION/, "");
          line = line.trim();
          if (result.parsedSequence) {
            result.parsedSequence.version = line;
          }
          break;
        case "COMMENT":
          line = line.replace(/COMMENT/, "");
          line = line.trim();
          if (result.parsedSequence) {
            if (!result.parsedSequence.comments) {
              result.parsedSequence.comments = [];
            }
            if (line.indexOf("teselagen_unique_id:") > -1) {
              result.parsedSequence.teselagen_unique_id = line.replace(/ /g, "").replace("teselagen_unique_id:", "");
            } else if (line.indexOf("library:") > -1) {
              result.parsedSequence.library = line.replace(/ /g, "").replace("library:", "");
            } else {
              result.parsedSequence.comments.push(line);
            }
          } else {
            throw new Error(
              "no sequence yet created upon which to extract an extra line!"
            );
          }
          break;
        default:
          extractExtraLine(line);
          if (key === "BASE") {
            addMessage(
              "Warning: This BaseCount line has been ignored: " + line
            );
            break;
          } else if (isKey) {
          } else if (isSubKey) {
          } else if (isKeyRunon) {
          } else {
            addMessage("Warning: This line has been ignored: " + line);
          }
      }
      return false;
    });
  } catch (e) {
    console.error("Error trying to parse file as .gb:", e);
    result = {
      success: false,
      messages: ["Import Error: Invalid File"]
    };
  }
  if (!result || result.success && resultsArray[resultsArray.length - 1] !== result) {
    endSeq();
  }
  const results = validateSequenceArray(
    flattenSequenceArray(resultsArray, options),
    options
  );
  results.forEach((result2) => {
    if (result2.success) {
      const sequence = result2.parsedSequence;
      sequence.features.forEach((feat) => {
        if (feat.type === "primer") {
          feat.type = "primer_bind";
        }
      });
      if (!options.primersAsFeatures) {
        sequence.primers = sequence.features.filter(
          (feat) => feat.type === "primer_bind"
        );
        sequence.features = sequence.features.filter(
          (feat) => feat.type !== "primer_bind"
        );
      }
    }
  });
  return results;
  function endSeq(options2) {
    hasFoundLocus = false;
    postProcessCurSeq(options2);
    resultsArray.push(result || { success: false });
  }
  function getCurrentFeature() {
    return result.parsedSequence.features[result.parsedSequence.features.length - 1];
  }
  function addMessage(msg) {
    if (result.messages.indexOf(msg === -1)) {
      return result.messages.push(msg);
    }
  }
  function postProcessCurSeq(options2) {
    if (result && result.parsedSequence && result.parsedSequence.features) {
      for (let i = 0; i < result.parsedSequence.features.length; i++) {
        result.parsedSequence.features[i] = postProcessGenbankFeature(
          result.parsedSequence.features[i],
          result.parsedSequence,
          options2
        );
      }
    }
  }
  function parseOrigin(line, key) {
    if (key !== genbankAnnotationKey.ORIGIN_TAG) {
      const new_line = line.replace(/[\s]*[0-9]*/g, "");
      result.parsedSequence.sequence += new_line;
    }
  }
  function parseLocus(line) {
    result = createInitialSequence(options);
    let circular;
    let gbDivision;
    let date;
    const lineArr = line.split(/[\s]+/g);
    if (lineArr.length <= 1) {
      console.warn(
        "Parsing GenBank File: WARNING! Locus line contains no values!"
      );
      addMessage("Import Warning: Locus line contains no values: " + line);
    }
    const locusName = lineArr[1];
    for (let i = 1; i < lineArr.length; i++) {
      if (lineArr[i].match(/circular/gi)) {
        circular = true;
      } else if (lineArr[i].match(/linear/gi)) {
        circular = false;
      }
    }
    for (let j = 1; j < lineArr.length; j++) {
      const item = lineArr[j];
      if (item.match(/-[A-Z]{3}-/g)) {
        date = item;
      }
      if (j === 3 && item.match(/aa/i)) {
        options.sequenceTypeFromLocus = item;
        options.isProtein = true;
      }
      if (j === 4 && (item.match(/ds-dna/i) || item.match(/ss-dna/i) || item.match(/dna/i) || item.match(/rna/i))) {
        if (options.isProtein === void 0) {
          options.isProtein = false;
        }
        options.sequenceTypeFromLocus = item;
        if (item.match(/ss-dna/i)) {
          options.isDNA = true;
          options.isSingleStrandedDNA = true;
        } else if (item.match(/rna/i)) {
          options.isRna = true;
        } else if (item.match(/ds-dna/i) || item.match(/dna/i)) {
          options.isDNA = true;
          options.isDoubleStrandedDNA = true;
        }
        if (item.match(/rna/i) && !item.match(/ss-rna/i)) {
          options.isDoubleStrandedRNA = true;
        }
      }
      if (typeof lineArr[j] === "string" && gbDivisions[lineArr[j].toUpperCase()]) {
        gbDivision = lineArr[j].toUpperCase();
      }
    }
    if (locusName !== "Exported" || result.parsedSequence.name === untitledSequenceName) {
      result.parsedSequence.name = locusName;
    }
    result.parsedSequence.gbDivision = gbDivision;
    result.parsedSequence.sequenceTypeFromLocus = options.sequenceTypeFromLocus;
    result.parsedSequence.isDNA = options.isDNA;
    result.parsedSequence.isDoubleStrandedDNA = options.isDoubleStrandedDNA;
    result.parsedSequence.isSingleStrandedDNA = options.isSingleStrandedDNA;
    result.parsedSequence.isRna = options.isRna;
    result.parsedSequence.isDoubleStrandedRNA = options.isDoubleStrandedRNA;
    result.parsedSequence.date = date;
    result.parsedSequence.circular = circular;
  }
  function extractExtraLine(line) {
    if (result.parsedSequence) {
      if (!result.parsedSequence.extraLines) {
        result.parsedSequence.extraLines = [];
      }
      result.parsedSequence.extraLines.push(line);
    } else {
      throw new Error(
        "no sequence yet created upon which to extract an extra line!"
      );
    }
  }
  var lastLineWasFeaturesTag;
  var lastLineWasLocation;
  function parseFeatures(line, key, val) {
    let strand;
    if (key === genbankAnnotationKey.FEATURES_TAG) {
      lastLineWasFeaturesTag = true;
      return;
    }
    if (lastLineWasFeaturesTag) {
      featureLocationIndentation = getLengthOfWhiteSpaceBeforeStartOfLetters(line);
      lastLineWasFeaturesTag = false;
    }
    if (isFeatureLineRunon(line, featureLocationIndentation)) {
      if (lastLineWasLocation) {
        const feat = getCurrentFeature();
        feat.locations = feat.locations.concat(
          parseFeatureLocation(
            line.trim(),
            options.isProtein,
            inclusive1BasedStart,
            inclusive1BasedEnd,
            result.parsedSequence.circular,
            result.parsedSequence.sequence.length
          )
        );
        lastLineWasLocation = true;
      } else {
        if (currentFeatureNote) {
          currentFeatureNote[currentFeatureNote.length - 1] += line.trimLeft().replace(/"/g, "");
        }
        lastLineWasLocation = false;
      }
    } else {
      if (isNote(line)) {
        if (getCurrentFeature()) {
          parseFeatureNote(line);
          lastLineWasLocation = false;
        } else {
          return;
        }
      } else {
        if (val.match(/complement/g)) {
          strand = -1;
        } else {
          strand = 1;
        }
        newFeature();
        const feat = getCurrentFeature();
        feat.type = key;
        feat.strand = strand;
        feat.forward = strand === 1;
        feat.locations = feat.locations.concat(
          parseFeatureLocation(
            val,
            options.isProtein,
            inclusive1BasedStart,
            inclusive1BasedEnd,
            result.parsedSequence.circular,
            result.parsedSequence.sequence.length
          )
        );
        lastLineWasLocation = true;
      }
    }
  }
  function newFeature() {
    result.parsedSequence.features.push({
      locations: [],
      notes: {}
    });
  }
  function isNote(line) {
    let qual = false;
    if (line.trim().charAt(0).match(/\//)) {
      qual = true;
    } else if (line.match(/^[\s]*\/[\w]+=[\S]+/)) {
      qual = true;
    }
    return qual;
  }
  function parseFeatureNote(line) {
    let newLine;
    newLine = line.trimLeft();
    newLine = newLine.replace(/^\/|"$/g, "");
    const lineArr = newLine.split(/="|=/);
    let val = lineArr.slice(1).join("=");
    if (val) {
      val = val.replace(/\\/g, " ");
      if (line.match(/="/g)) {
        val = val.replace(/".*/g, "");
      } else if (val.match(/^\d+$/g)) {
        val = parseInt(val, 10);
      }
    }
    const key = lineArr[0];
    const currentNotes = getCurrentFeature().notes;
    if (currentNotes[key]) {
      currentNotes[key].push(val);
    } else {
      currentNotes[key] = [val];
    }
    currentFeatureNote = currentNotes[key];
  }
  function getLineKey(line, shouldUseSpaceAsDelimiter) {
    let arr;
    line = line.replace(/^[\s]*/, "");
    if (line.indexOf("=") < 0 || shouldUseSpaceAsDelimiter) {
      arr = line.split(/[\s]+/);
    } else {
      arr = line.split(/=/);
    }
    return arr[0];
  }
  function getLineVal(line, shouldUseSpaceAsDelimiter) {
    let arr;
    if (line.indexOf("=") < 0 || shouldUseSpaceAsDelimiter) {
      line = line.replace(/^[\s]*[\S]+[\s]+|[\s]+$/, "");
      line = line.trim();
      return line;
    } else {
      arr = line.split(/=/);
      return arr.slice(1).join("");
    }
  }
  function isKeyword(line) {
    let isKey = false;
    if (line.substr(0, 10).match(/^[\S]+/)) {
      isKey = true;
    }
    return isKey;
  }
  function isSubKeyword(line) {
    let isSubKey = false;
    if (line.substr(0, 10).match(/^[\s]+[\S]+/)) {
      isSubKey = true;
    }
    return isSubKey;
  }
  function isKeywordRunon(line) {
    let runon;
    if (line.substr(0, 10).match(/[\s]{10}/)) {
      runon = true;
    } else {
      runon = false;
    }
    return runon;
  }
  function postProcessGenbankFeature(feat, parsedSequence, options2) {
    if (feat.notes.label) {
      feat.name = feat.notes.label[0];
    } else if (feat.notes.gene) {
      feat.name = feat.notes.gene[0];
    } else if (feat.notes.ApEinfo_label) {
      feat.name = feat.notes.ApEinfo_label[0];
    } else if (feat.notes.name) {
      feat.name = feat.notes.name[0];
    } else if (feat.notes.organism) {
      feat.name = feat.notes.organism[0];
    } else if (feat.notes.locus_tag) {
      feat.name = feat.notes.locus_tag[0];
    } else if (feat.notes.note) {
      feat.name = feat.notes.note[0].substr(0, 100);
    } else {
      feat.name = "Untitled Feature";
    }
    feat.name = typeof feat.name === "string" ? feat.name : String(feat.name);
    if (feat.name !== 0 && !feat.name) {
      feat.name = "Untitled Feature";
    }
    if (feat.name.length > 100) {
      const oldName = feat.name;
      feat.name = feat.name.substr(0, 100);
      addMessage(
        `Warning: Shortening name of feature ${oldName} (max 100 chars)`
      );
    }
    if (feat.notes.direction) {
      feat.arrowheadType = feat.notes.direction[0].toUpperCase() === "BOTH" ? "BOTH" : feat.notes.direction[0].toUpperCase() === "NONE" ? "NONE" : void 0;
      delete feat.notes.direction;
    }
    if (feat.locations && feat.locations.length > 1 && feat.locations[0].start > feat.locations[feat.locations.length - 1].end) {
      if (parsedSequence.circular === void 0) {
        parsedSequence.circular = true;
      }
    }
    if (parsedSequence.circular) {
      const { inclusive1BasedStart: inclusive1BasedStart2, inclusive1BasedEnd: inclusive1BasedEnd2 } = options2;
      feat.locations = wrapOriginSpanningFeatures(
        feat.locations,
        parsedSequence.sequence.length,
        inclusive1BasedStart2,
        inclusive1BasedEnd2
      );
    } else if (feat.locations && feat.locations.length > 1) {
      feat.locations.sort((a, b) => a.start - b.start);
    }
    return feat;
  }
}
function isFeatureLineRunon(line, featureLocationIndentation) {
  const indentationOfLine = getLengthOfWhiteSpaceBeforeStartOfLetters(line);
  if (featureLocationIndentation === indentationOfLine) {
    return false;
  }
  const trimmed = line.trim();
  if (trimmed.charAt(0).match(/\//)) {
    return false;
  }
  return true;
}
function getLengthOfWhiteSpaceBeforeStartOfLetters(string) {
  const match = /^\s*/.exec(string);
  if (match !== null) {
    return match[0].length;
  } else {
    return 0;
  }
}
export default genbankToJson;
